// KPI kanıt belgeleri — /api/v1/kpi/evidence/* + /kpi/values/*/evidence typed wrapper'ları
// (M16 T18; route tablosu: routes/kpi.ts "Kanıt belgeleri" bloğu). Yükleme akışı M11 attachments
// ile aynı: presign → PUT (proxy) → commit → link. `setKpiValuePinned` api/kpi.ts'te (T23).

import type { EvidenceDocument, KpiEvidenceLocator, KpiEvidenceRelationType } from "@mahir/shared";
import { api, uploadFileWithProgress } from "./client";

export interface KpiLinkedEvidence extends EvidenceDocument {
  relationType: KpiEvidenceRelationType;
  locator: KpiEvidenceLocator | null;
  linkedBy: string | null;
  linkedAt: string;
  downloadUrl?: string;
}

export interface KpiEvidenceTarget {
  metricId: string;
  entityId: string;
  year: number;
}

export async function presignKpiEvidence(body: {
  clientId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sourceType?: "upload" | "import" | "manual";
}): Promise<{ evidence: EvidenceDocument; uploadUrl: string; expiresIn: number }> {
  return api("/kpi/evidence/presign", { method: "POST", body });
}

export async function commitKpiEvidence(evidenceId: string): Promise<EvidenceDocument> {
  const res = await api<{ evidence: EvidenceDocument }>(`/kpi/evidence/${evidenceId}/commit`, {
    method: "POST",
  });
  return res.evidence;
}

export async function getKpiEvidence(evidenceId: string): Promise<{
  evidence: EvidenceDocument;
  downloadUrl: string;
  targets: (KpiEvidenceTarget & { periodType?: string })[];
}> {
  return api(`/kpi/evidence/${evidenceId}`);
}

export async function listClientKpiEvidence(clientId: string): Promise<EvidenceDocument[]> {
  const res = await api<{ evidence: EvidenceDocument[] }>(`/kpi/clients/${clientId}/evidence`);
  return res.evidence;
}

export async function linkKpiEvidence(body: {
  evidenceId: string;
  targets: KpiEvidenceTarget[];
  locator?: KpiEvidenceLocator;
  relationType?: KpiEvidenceRelationType;
}): Promise<{ linked: number; valueIds: string[] }> {
  return api("/kpi/values/evidence-links", { method: "POST", body });
}

export async function listKpiValueEvidence(valueId: string): Promise<KpiLinkedEvidence[]> {
  const res = await api<{ evidence: KpiLinkedEvidence[] }>(`/kpi/values/${valueId}/evidence`);
  return res.evidence;
}

/** TODO-EVIDENCE-03 — dosya bazlı saklama politikası: system | keep | <n>d. */
export async function updateKpiEvidenceRetention(
  evidenceId: string,
  retentionPolicy: string,
): Promise<EvidenceDocument> {
  const res = await api<{ evidence: EvidenceDocument }>(`/kpi/evidence/${evidenceId}`, {
    method: "PATCH",
    body: { retentionPolicy },
  });
  return res.evidence;
}

export async function unlinkKpiEvidence(
  valueId: string,
  evidenceId: string,
): Promise<{ removed: boolean }> {
  return api(`/kpi/values/${valueId}/evidence/${evidenceId}`, { method: "DELETE" });
}

/** İç kullanıcı proxy indirme adresi (backend `evidenceDownloadUrl` ile aynı kalıp). */
export function kpiEvidenceDownloadUrl(
  doc: Pick<EvidenceDocument, "storagePath" | "fileName">,
): string {
  return `/api/v1/files/${doc.storagePath}?filename=${encodeURIComponent(doc.fileName)}`;
}

/** presign → PUT → commit; ilerleme yüzdesi isteğe bağlı. Dönen belge `scanStatus` taşır. */
export async function uploadKpiEvidence(
  clientId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<EvidenceDocument> {
  const pre = await presignKpiEvidence({
    clientId,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    sourceType: "upload",
  });
  await uploadFileWithProgress(pre.uploadUrl, file, onProgress);
  return commitKpiEvidence(pre.evidence.id);
}
